import type {
  History,
  LocationDescriptor,
  LocationDescriptorObject,
} from "history";

import { type MobileFunctions } from "./types";
import {
  emitWebviewNavigationEvent,
  handleWebviewBack,
} from "./webviewNavigationBridge";

function setupMobileNavigateFunctions(baseHistory: History) {
  const origin = window.location.origin;
  const basename = baseHistory.createHref({ pathname: "/" }).replace(/\/$/, "");

  // The URL from RN may be a full URL or a path that includes the basename (e.g., /app/).
  // Since baseHistory was created with a basename, we need to strip it to avoid
  // double-prefixing (e.g., /app/app/...). Also strip the origin if it's a full URL.
  const stripPrefixes = (url: string): string => {
    let path = url;
    if (path.startsWith(origin)) {
      path = path.slice(origin.length);
    }
    if (basename && path.startsWith(basename + "/")) {
      path = path.slice(basename.length);
    } else if (path === basename) {
      path = "/";
    }
    return path;
  };

  // Register a global callback that React Native can invoke via injectJavaScript
  // to navigate within the SPA without a full page reload. Uses baseHistory directly
  // (not shimmedHistory) to avoid re-emitting onNavigationEvent back to RN.
  (window as unknown as MobileFunctions)._webNavigate = (url, _type, state) => {
    const path = stripPrefixes(url);

    // Always uses replace: by the time RN calls _webNavigate, the shimmed history
    // has already performed the original push/replace (it runs synchronously before
    // the async postMessage reaches RN). We're correcting the URL of the entry the
    // web already created, not adding a new one.
    baseHistory.replace(path, state);
  };
}

/**
 * Wraps a browser history object with webview navigation integration.
 *
 * - Emits `window.onNavigationEvent()` BEFORE routing occurs for push/replace
 * - Delegates all back navigation to native `window.onGoBack()`
 * - Registers `window._webNavigate` so React Native can navigate the SPA
 *   without a full page reload (uses baseHistory directly to avoid
 *   re-emitting onNavigationEvent back to RN)
 */
export function createWebviewHistoryWrapper(baseHistory: History): History {
  setupMobileNavigateFunctions(baseHistory);

  const normalizeLocation = (
    pathOrLocation: LocationDescriptor,
  ): LocationDescriptorObject => {
    if (typeof pathOrLocation === "string") {
      return { pathname: pathOrLocation };
    }
    return pathOrLocation;
  };

  const shimmedHistory: History = {
    ...baseHistory,

    push(pathOrLocation: LocationDescriptor, state?: unknown) {
      const location = normalizeLocation(pathOrLocation);
      if (state !== undefined) {
        location.state = state;
      }

      const url = baseHistory.createHref({
        pathname: location.pathname ?? "",
        search: location.search ?? "",
        hash: location.hash ?? "",
      });
      emitWebviewNavigationEvent(url, "push", location.state);

      return baseHistory.push(pathOrLocation, state);
    },

    replace(pathOrLocation: LocationDescriptor, state?: unknown) {
      const location = normalizeLocation(pathOrLocation);
      if (state !== undefined) {
        location.state = state;
      }

      const url = baseHistory.createHref({
        pathname: location.pathname ?? "",
        search: location.search ?? "",
        hash: location.hash ?? "",
      });
      emitWebviewNavigationEvent(url, "replace", location.state);

      return baseHistory.replace(pathOrLocation, state);
    },

    goBack() {
      if (!handleWebviewBack()) {
        baseHistory.goBack();
      }
    },

    go(n: number) {
      if (n < 0) {
        if (n < -1) {
          console.warn(
            `history.go(${n}) called in webview - only go(-1) is expected. Treating as single back navigation.`,
          );
        }
        if (!handleWebviewBack()) {
          baseHistory.go(n);
        }
        return;
      }
      return baseHistory.go(n);
    },
  };

  return shimmedHistory;
}
